import { useState } from 'react';
import { color, fontSize, radius } from '../../theme/tokens';

/**
 * Show SQL and Show plan (spec 006 T110, FR-A06).
 *
 * ## ⚠ Shown for AUDIT, never to be hand-edited and re-run
 *
 * That is a different product with a different authority (spec Out of Scope). The statement is
 * read-only text: a reader who distrusts a figure can see exactly what produced it, and that is
 * the whole purpose.
 *
 * ## ⚠ Without this, `sql` and the agent trace are recorded and never surfaced
 *
 * "Being wrong costs a re-run; being *silently* wrong costs trust in the dashboard beside it."
 * The plan is what lets a reader tell the two apart.
 */

export interface TurnDetailsProps {
  sql: string | null;
  tier: 0 | 1 | 2 | null;
  assumptions: readonly string[];
  /** ⚠ A visible marker: a tier-2 answer's statement was written by a model. */
  authored?: boolean;
}

export function TurnDetails({ sql, tier, assumptions, authored }: TurnDetailsProps) {
  const [showSql, setShowSql] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  // Tier 0 ran no query at all — offering "Show SQL" there would promise something that does
  // not exist, and an empty panel reads as a fault.
  const hasSql = Boolean(sql);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {hasSql ? (
          <DetailButton expanded={showSql} onClick={() => setShowSql((v) => !v)} label="Show SQL" />
        ) : null}
        <DetailButton expanded={showPlan} onClick={() => setShowPlan((v) => !v)} label="Show plan" />

        {/*
          ⚠ A visible marker on an authored answer. Tier 2 is the one path where a plausible wrong
          number is possible at all, and the reader is entitled to know which kind of answer they
          are looking at.
        */}
        {authored ? (
          <span
            style={{
              fontSize: fontSize.caption,
              color: color.inkMuted,
              border: `1px solid ${color.border}`,
              borderRadius: radius.md,
              padding: '1px 6px',
            }}
          >
            authored query
          </span>
        ) : null}
      </div>

      {showSql && sql ? (
        <pre
          style={{
            marginTop: 8,
            padding: 10,
            background: color.surfaceSubtle,
            border: `1px solid ${color.borderSubtle}`,
            borderRadius: radius.md,
            fontSize: fontSize.caption,
            color: color.ink,
            overflowX: 'auto',
            // Read-only by construction: a `<pre>` is not an editor, which is the point.
            whiteSpace: 'pre',
          }}
        >
          {sql}
        </pre>
      ) : null}

      {showPlan ? (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            background: color.surfaceSubtle,
            border: `1px solid ${color.borderSubtle}`,
            borderRadius: radius.md,
            fontSize: fontSize.caption,
            color: color.ink,
          }}
        >
          <div>
            Answered by:{' '}
            {tier === 0
              ? 'the dashboard’s own figures — no query was run'
              : tier === 2
                ? 'an authored query, reviewed before it ran'
                : 'a compiled query'}
          </div>
          {assumptions.length > 0 ? (
            <div style={{ marginTop: 6 }}>Assumed: {assumptions.join(' · ')}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DetailButton({
  expanded,
  onClick,
  label,
}: {
  expanded: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      style={{
        background: 'transparent',
        border: `1px solid ${color.border}`,
        borderRadius: radius.md,
        padding: '2px 8px',
        fontSize: fontSize.caption,
        color: color.inkMuted,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
